import {
  GetMissingFieldsLevelParameter,
  StakeholderSubjectType,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import { complianceEntitiesApi, onboardingApi, stakeholdersApi } from "@api";
import { FlowMEChangeContext } from "..";
import { useWSMutation, WSMutationConfig } from "../../../../../query/helpers";
import { useSession } from "@hooks/useSession";
import { useQueryPrimaryComplianceEntity } from "../../../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { selectorComplianceEntityCountry } from "../../../../../shared/selectors/selectorComplianceEntityCountry";
import { useFlowContext } from "../../../../../shared/components/Flow";
import { WSServiceError } from "../../../../../utils/serviceHelper";
import {
  getBusinessEntityData,
  getIndividualEntityData,
  onlyNewTaxIdentifiers,
  toEntityUpdate
} from "./helpers";
import {
  getVerificationState,
  VerificationState
} from "../../../utils/getVerificationState";
import { useRequiresBankingVerification } from "../../../hooks/useRequiresBankingVerification";
import { selectorIsEntityVerified } from "../../../selectors/selectorIsEntityVerified";
import { upsertStakeholderIdentity } from "../../../steps/CreateAccount/useMutationSubmit";

export const useMutationSubmitMaterial = (
  config?: WSMutationConfig<VerificationState, WSServiceError, void>
) => {
  const session = useSession();
  const [context] = useFlowContext<FlowMEChangeContext>();
  const requiresBanking = useRequiresBankingVerification();
  const queryCurrentEntity = useQueryPrimaryComplianceEntity();

  return useWSMutation<VerificationState, WSServiceError, void>(async () => {
    if (!context.accountType?.country) {
      throw new Error("No country set");
    }

    const accountId = session.accountId;
    const country = context.accountType.country;
    const verificationLevel = (
      requiresBanking ? "Banking" : "Tax"
    ) as GetMissingFieldsLevelParameter;

    const initialVerifications =
      (await onboardingApi.getOnboardingStatus({})).verifications ?? {};
    const isEntityVerified = selectorIsEntityVerified(initialVerifications);

    const currentEntity = queryCurrentEntity.data;

    if (!currentEntity?.id) {
      throw new Error("No compliance entity found");
    }

    const dataMatchesCurrentEntity =
      currentEntity.type === context.accountType.type &&
      selectorComplianceEntityCountry(currentEntity) === country;

    let entityData;
    if (context.accountType.type === "Individual") {
      if (!context.personalInformation) {
        throw new Error("No personal information set");
      }

      entityData = getIndividualEntityData({
        personFormData: context.personalInformation,
        usPersonFormData: context.usInternationalPersonInformation,
        country
      });
    } else {
      if (!context.businessInformation) {
        throw new Error("No business information set");
      }
      if (!context.representative) {
        throw new Error("No representative set");
      }

      entityData = getBusinessEntityData(context.businessInformation, country);
    }

    let newEntityCreated = false;

    if (isEntityVerified) {
      // A verified entity can't be edited in place — the change goes through the
      // advance transition, which supersedes it with a new version. v2 also took a
      // free-text reason for the change; v3 has no field for it.
      await complianceEntitiesApi.advanceComplianceEntity({
        complianceEntityId: currentEntity.id,
        complianceEntityAdvanceRequest: {
          ...entityData,
          effectiveDate: context.reason?.effectiveAt
        }
      });
      newEntityCreated = true;
    } else if (!dataMatchesCurrentEntity) {
      // Type or country changed, so the entity is replaced rather than edited.
      await complianceEntitiesApi.createComplianceEntity({
        complianceEntityCreate: entityData
      });
      newEntityCreated = true;
    } else {
      // Check verification status before submitting, to avoid overwriting
      // data that is already pending review or verified
      const preSubmitState = await getVerificationState(verificationLevel);

      if (
        preSubmitState.status === VerificationLevel.PENDING ||
        preSubmitState.status === VerificationLevel.VERIFIED
      ) {
        return preSubmitState;
      }

      await complianceEntitiesApi.updateComplianceEntity({
        complianceEntityId: currentEntity.id,
        complianceEntityUpdate: {
          ...toEntityUpdate(entityData),
          taxIdentifiers: onlyNewTaxIdentifiers(
            entityData.taxIdentifiers,
            currentEntity.taxIdentifiers
          )
        }
      });
    }

    // The representative is a stakeholder on v3, not a relationship on the entity.
    if (context.accountType.type !== "Individual" && context.representative) {
      const stakeholders = await stakeholdersApi.listAccountStakeholders({
        accountId
      });

      await upsertStakeholderIdentity({
        accountId,
        country,
        existing: (stakeholders.data ?? []).find(
          stakeholder =>
            stakeholder.subjectType === StakeholderSubjectType.PERSON &&
            stakeholder.isPrincipal
        ),
        subjectType: StakeholderSubjectType.PERSON,
        isPrincipal: true,
        isController: true,
        person: context.representative.representative
      });
    }

    if (newEntityCreated) {
      await onboardingApi.triggerVerification({
        verifyRequest: { level: verificationLevel }
      });
    }

    return getVerificationState(verificationLevel);
  }, config);
};
